/**
 * Booking Lifecycle Management
 * Handles stage transitions, validation, and history logging
 */

import { supabaseAdmin } from '@/integrations/supabase/client'
import { cleanStageHistory } from '@/lib/booking/stageHistory'

// Lifecycle stage constants
export const LIFECYCLE_STAGES = {
  BOOKED: 'booked',
  LOGISTICS_COMPLETED: 'logistics_questionnaire_completed',
  COMMUNICATION_IN_PROGRESS: 'client_talent_communication_in_progress',
  TALENT_CHECK_IN: 'talent_5_day_check_in',
  EVENT_DAY: 'event_day',
  MEDIA_UPLOADED: 'media_uploaded',
  MEDIA_ISSUE: 'media_issue',
  MEDIA_APPROVED: 'media_approved',
  TALENT_PAID: 'talent_paid',
  MEDIA_DELIVERED_PARTIAL: 'media_delivered_partial',
  MEDIA_DELIVERED_FULL: 'media_delivered_full',
  BOOKING_COMPLETE: 'booking_complete',
} as const

export type LifecycleStage = typeof LIFECYCLE_STAGES[keyof typeof LIFECYCLE_STAGES]

// Stage labels for UI display
export const STAGE_LABELS: Record<LifecycleStage, string> = {
  [LIFECYCLE_STAGES.BOOKED]: 'Booked',
  [LIFECYCLE_STAGES.LOGISTICS_COMPLETED]: 'Logistics Questionnaire Completed',
  [LIFECYCLE_STAGES.COMMUNICATION_IN_PROGRESS]: 'Client/Talent Communication in Progress',
  [LIFECYCLE_STAGES.TALENT_CHECK_IN]: 'Talent 5-Day Check-In',
  [LIFECYCLE_STAGES.EVENT_DAY]: 'Event Day',
  [LIFECYCLE_STAGES.MEDIA_UPLOADED]: 'Media Uploaded',
  [LIFECYCLE_STAGES.MEDIA_ISSUE]: 'Media Issue',
  [LIFECYCLE_STAGES.MEDIA_APPROVED]: 'Media Approved / Media in Progress',
  [LIFECYCLE_STAGES.TALENT_PAID]: 'Talent Paid',
  [LIFECYCLE_STAGES.MEDIA_DELIVERED_PARTIAL]: 'Media Delivered (Partial)',
  [LIFECYCLE_STAGES.MEDIA_DELIVERED_FULL]: 'Media Delivered (Full)',
  [LIFECYCLE_STAGES.BOOKING_COMPLETE]: 'Booking Complete',
}

// Stage colors for UI
export const STAGE_COLORS: Record<LifecycleStage, string> = {
  [LIFECYCLE_STAGES.BOOKED]: 'bg-pink-200',
  [LIFECYCLE_STAGES.LOGISTICS_COMPLETED]: 'bg-purple-200',
  [LIFECYCLE_STAGES.COMMUNICATION_IN_PROGRESS]: 'bg-blue-200',
  [LIFECYCLE_STAGES.TALENT_CHECK_IN]: 'bg-indigo-200',
  [LIFECYCLE_STAGES.EVENT_DAY]: 'bg-yellow-200',
  [LIFECYCLE_STAGES.MEDIA_UPLOADED]: 'bg-pink-400',
  [LIFECYCLE_STAGES.MEDIA_ISSUE]: 'bg-red-500',
  [LIFECYCLE_STAGES.MEDIA_APPROVED]: 'bg-green-300',
  [LIFECYCLE_STAGES.TALENT_PAID]: 'bg-emerald-300',
  [LIFECYCLE_STAGES.MEDIA_DELIVERED_PARTIAL]: 'bg-teal-300',
  [LIFECYCLE_STAGES.MEDIA_DELIVERED_FULL]: 'bg-cyan-300',
  [LIFECYCLE_STAGES.BOOKING_COMPLETE]: 'bg-green-500',
}

// Get all stages in order
export const STAGE_ORDER: LifecycleStage[] = [
  LIFECYCLE_STAGES.BOOKED,
  LIFECYCLE_STAGES.LOGISTICS_COMPLETED,
  LIFECYCLE_STAGES.COMMUNICATION_IN_PROGRESS,
  LIFECYCLE_STAGES.TALENT_CHECK_IN,
  LIFECYCLE_STAGES.EVENT_DAY,
  LIFECYCLE_STAGES.MEDIA_UPLOADED,
  LIFECYCLE_STAGES.MEDIA_ISSUE,
  LIFECYCLE_STAGES.MEDIA_APPROVED,
  LIFECYCLE_STAGES.TALENT_PAID,
  LIFECYCLE_STAGES.MEDIA_DELIVERED_PARTIAL,
  LIFECYCLE_STAGES.MEDIA_DELIVERED_FULL,
  LIFECYCLE_STAGES.BOOKING_COMPLETE,
]

/**
 * Determine the booking_status based on lifecycle_stage
 * - 'confirmed' stays until communication starts
 * - 'in_progress' from communication onwards until booking_complete
 * - 'completed' only at booking_complete
 */
function getBookingStatusForStage(stage: LifecycleStage): string | null {
  // Stages that indicate booking is "in progress" (event workflow has started)
  const inProgressStages: LifecycleStage[] = [
    LIFECYCLE_STAGES.COMMUNICATION_IN_PROGRESS,
    LIFECYCLE_STAGES.TALENT_CHECK_IN,
    LIFECYCLE_STAGES.EVENT_DAY,
    LIFECYCLE_STAGES.MEDIA_UPLOADED,
    LIFECYCLE_STAGES.MEDIA_ISSUE,
    LIFECYCLE_STAGES.MEDIA_APPROVED,
    LIFECYCLE_STAGES.TALENT_PAID,
    LIFECYCLE_STAGES.MEDIA_DELIVERED_PARTIAL,
    LIFECYCLE_STAGES.MEDIA_DELIVERED_FULL,
  ]

  if (stage === LIFECYCLE_STAGES.BOOKING_COMPLETE) {
    return 'completed'
  } else if (inProgressStages.includes(stage)) {
    return 'in_progress'
  }

  // For earlier stages (booked, logistics_completed), don't change booking_status
  // It will stay as 'confirmed' (set when talent confirms)
  return null
}

/**
 * Update a booking's lifecycle stage
 * This function updates both the bookings table AND logs to booking_stage_history
 * Also updates booking_status based on lifecycle stage progression
 */
export async function updateBookingStage(
  bookingId: string,
  toStage: LifecycleStage,
  userId: string | null,
  reason?: string,
  changeType: string = 'auto'
) {
  try {
    // First, get the current stage and booking_status
    const { data: currentBooking, error: fetchError } = await supabaseAdmin
      .from('bookings')
      .select('lifecycle_stage, booking_status')
      .eq('id', bookingId)
      .single()

    if (fetchError) throw fetchError

    const fromStage = currentBooking?.lifecycle_stage as LifecycleStage | null
    const currentStatus = currentBooking?.booking_status

    // Determine if booking_status should be updated based on new lifecycle stage
    const newBookingStatus = getBookingStatusForStage(toStage)

    // Build update object
    const updateData: Record<string, any> = {
      lifecycle_stage: toStage,
      stage_updated_at: new Date().toISOString(),
      stage_updated_by: userId,
      stage_override_reason: reason || null,
    }

    // Only update booking_status if:
    // 1. We have a new status to set
    // 2. Current status is not 'cancelled' or 'declined_by_talent' (preserve those)
    if (newBookingStatus &&
        currentStatus !== 'cancelled' &&
        currentStatus !== 'declined_by_talent') {
      updateData.booking_status = newBookingStatus
    }

    // A reason typed by hand is cleared first, so the history trigger sees it as new even when it matches the last
    // one (it only marks a change manual when this update sets a new reason)
    if (reason) await supabaseAdmin.from('bookings').update({ stage_override_reason: null }).eq('id', bookingId)

    // Update the booking stage
    const { data, error } = await supabaseAdmin
      .from('bookings')
      .update(updateData)
      .eq('id', bookingId)
      .select()
      .single()

    if (error) throw error

    // The database trigger (log_booking_stage_change) writes the history row for this change; writing one here too
    // made every change show twice in Lifecycle History (Traci, 5 Oct 2026).

    return { success: true, data }
  } catch (error) {
    console.error('Error updating booking stage:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Get stage history for a booking
 */
export async function getBookingStageHistory(bookingId: string) {
  try {
    const { data, error } = await supabaseAdmin
      .from('booking_stage_history')
      .select(`
        *,
        changed_by_user:changed_by (
          id,
          full_name,
          email
        )
      `)
      .eq('booking_id', bookingId)
      .order('changed_at', { ascending: false })

    if (error) throw error

    return { success: true, data: cleanStageHistory(data || []) }
  } catch (error) {
    console.error('Error fetching stage history:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      data: []
    }
  }
}

/**
 * Get current stage index (for progress calculation)
 */
export function getStageIndex(stage: LifecycleStage): number {
  return STAGE_ORDER.indexOf(stage)
}

/**
 * Calculate progress percentage
 */
export function getStageProgress(stage: LifecycleStage): number {
  const index = getStageIndex(stage)
  return Math.round((index / (STAGE_ORDER.length - 1)) * 100)
}
